import React, { useMemo, useState } from 'react';
import {
  DateTimePreview,
  FormGroup,
  Input,
  Label,
} from '../../../../styles/lesson/lesson.style';
import {
  useLessonCreationState,
  useLessonCreationDispatch,
} from '../lessonCreation.state';
import { formatInTimeZone } from 'date-fns-tz';
import { wallClockToISO, wallClockEndToISO, shortTzName, friendlyTzName, getDetectedTimezone, formatRangeInTz } from '../../../../utils/timezones';
import { getTimezoneOffset, adjustAvailabilityForTimezone, DAYS_SUNDAY_FIRST, GRANULARITY } from '../../../../utils/availability';
import { AvailabilityObject } from '../../../../types/schemas/abc';
import { Globe } from 'lucide-react';

// --- Utils ---

const blockPickerHotkeys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  const isSpace = e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';
  const isEnter = e.key === 'Enter';
  const isAltDown = e.altKey && e.key === 'ArrowDown';
  if (isSpace || isEnter || isAltDown) {
    e.preventDefault();
    e.stopPropagation();
  }
};

const DAY_NAMES_LOWER: (keyof AvailabilityObject)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

const minutesToTimeStr = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Get tutor's availability slots for a specific day-of-week, shifted to the active input tz */
const getSlotsForDay = (
  tutorAvailability: AvailabilityObject | null,
  dayIndex: number,
  tutorTz: string,
  inputTz: string,
): { start: number; end: number }[] => {
  if (!tutorAvailability) return [];

  let avail = tutorAvailability;
  if (inputTz !== tutorTz) {
    const offset = getTimezoneOffset(inputTz, tutorTz);
    avail = adjustAvailabilityForTimezone(tutorAvailability, offset, DAYS_SUNDAY_FIRST);
  }

  const dayName = DAY_NAMES_LOWER[dayIndex];
  return (avail[dayName] || []).map(s => ({ start: s.start, end: s.end }));
};

const isTimeInSlots = (timeMinutes: number, endMinutes: number, slots: { start: number; end: number }[]): boolean => {
  return slots.some(s => timeMinutes >= s.start && endMinutes <= s.end);
};

// --- Component ---

export const Step2_FirstLesson: React.FC = () => {
  const { lessonDate, lessonTime, lessonEndTime, tutorTimezone, tutorAvailability, tutorName, user, existingEvents, tutorBusySlots, activeInputTz } = useLessonCreationState();
  const dispatch = useLessonCreationDispatch();

  const parentHomeTz = user.timezone || 'Europe/London';
  const browserTz = useMemo(() => getDetectedTimezone(), []);

  // activeInputTz is from state — defaults to parent's home tz
  const inputTz = activeInputTz;
  const tutorTzDiffers = inputTz !== tutorTimezone;

  // Build tz options with merged labels for matching timezones
  const tzEntries = useMemo(() => {
    const tzMap = new Map<string, { key: string; parts: string[] }>();

    const homeEntry = tzMap.get(parentHomeTz) || { key: 'home', parts: [] };
    homeEntry.parts.push('Home');
    tzMap.set(parentHomeTz, homeEntry);

    const browserEntry = tzMap.get(browserTz) || { key: 'browser', parts: [] };
    browserEntry.parts.push('Current');
    tzMap.set(browserTz, browserEntry);

    if (tutorTimezone) {
      const tutorEntry = tzMap.get(tutorTimezone) || { key: 'tutor', parts: [] };
      tutorEntry.parts.push("Tutor's");
      tzMap.set(tutorTimezone, tutorEntry);
    }

    return Array.from(tzMap.entries()).map(([tz, { key, parts }]) => ({
      key,
      tz,
      label: `${parts.join(' / ')} timezone (${friendlyTzName(tz)})`,
    }));
  }, [parentHomeTz, browserTz, tutorTimezone]);

  // Buttons = all entries except the one matching the current inputTz
  const tzSwitchButtons = tzEntries.filter(e => e.tz !== inputTz);

  // --- Handlers ---
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_LESSON_DATE', payload: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_LESSON_TIME', payload: e.target.value });
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_LESSON_END_TIME', payload: e.target.value });
  };

  // End time validation
  const crossesMidnight = !!(lessonTime && lessonEndTime && lessonEndTime < lessonTime);
  const endEqualsStart = !!(lessonTime && lessonEndTime && lessonEndTime === lessonTime);
  const endBeforeStart = endEqualsStart;

  // --- Preview in YOUR timezone ---
  const lessonTimePreview = useMemo(() => {
    if (lessonDate && lessonTime && lessonEndTime) {
      try {
        const startUtc = new Date(wallClockToISO(lessonDate, lessonTime, inputTz));
        const endUtc = new Date(wallClockEndToISO(lessonDate, lessonTime, lessonEndTime, inputTz));
        if (!isNaN(startUtc.getTime()) && !isNaN(endUtc.getTime())) {
          const startFormatted = formatInTimeZone(startUtc, inputTz, 'EEEE d MMMM yyyy, HH:mm');
          const endFormatted = formatInTimeZone(endUtc, inputTz, 'HH:mm');
          const nextDay = crossesMidnight ? ' (+1 day)' : '';
          return `${startFormatted} – ${endFormatted}${nextDay}`;
        }
      } catch { /* invalid date */ }
    }
    return '';
  }, [lessonDate, lessonTime, lessonEndTime, inputTz, crossesMidnight]);

  // --- Preview in TUTOR's timezone (only if different) ---
  const tutorTimePreview = useMemo(() => {
    if (!tutorTzDiffers || !lessonDate || !lessonTime || !lessonEndTime) return null;
    try {
      const startUtc = new Date(wallClockToISO(lessonDate, lessonTime, inputTz));
      const endUtc = new Date(wallClockEndToISO(lessonDate, lessonTime, lessonEndTime, inputTz));
      return formatRangeInTz(startUtc, endUtc, tutorTimezone, { includeDate: true });
    } catch { return null; }
  }, [tutorTzDiffers, lessonDate, lessonTime, lessonEndTime, inputTz, tutorTimezone]);

  // Validate lesson doesn't start in the past
  const startInPast = useMemo(() => {
    if (!lessonDate || !lessonTime) return false;
    try {
      return new Date(wallClockToISO(lessonDate, lessonTime, inputTz)).getTime() <= Date.now();
    } catch { return false; }
  }, [lessonDate, lessonTime, inputTz]);

  const today = new Date().toISOString().split('T')[0];

  // --- Availability checking (tutor's slots shown in the input tz) ---
  const selectedDayIndex = useMemo(() => {
    if (!lessonDate) return -1;
    const [y, m, d] = lessonDate.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }, [lessonDate]);

  const tutorSlotsForDay = useMemo(() => {
    if (selectedDayIndex < 0) return [];
    return getSlotsForDay(tutorAvailability, selectedDayIndex, tutorTimezone, inputTz);
  }, [tutorAvailability, selectedDayIndex, tutorTimezone, inputTz]);

  const outsideAvailability = useMemo(() => {
    if (!lessonTime || !lessonEndTime || tutorSlotsForDay.length === 0) return false;
    const [sh, sm] = lessonTime.split(':').map(Number);
    const [eh, em] = lessonEndTime.split(':').map(Number);
    return !isTimeInSlots(sh * 60 + sm, eh * 60 + em, tutorSlotsForDay);
  }, [lessonTime, lessonEndTime, tutorSlotsForDay]);

  // --- Conflict checking ---
  const conflictMessage = useMemo(() => {
    if (!lessonDate || !lessonTime || !lessonEndTime) return null;
    try {
      const newStart = wallClockToISO(lessonDate, lessonTime, inputTz);
      const newEnd = wallClockEndToISO(lessonDate, lessonTime, lessonEndTime, inputTz);

      const parentConflict = existingEvents.find(ev => ev.startAt < newEnd && ev.endAt > newStart);
      if (parentConflict) return `You already have "${parentConflict.title}" booked at this time.`;

      const tutorConflict = tutorBusySlots.find(s => s.startAt < newEnd && s.endAt > newStart);
      if (tutorConflict) return `${tutorName || 'The tutor'} already has a booking at this time.`;

      return null;
    } catch { return null; }
  }, [lessonDate, lessonTime, lessonEndTime, inputTz, existingEvents, tutorBusySlots, tutorName]);

  // --- Availability chips (shown in input tz) ---
  const availabilityChips = useMemo(() => {
    if (tutorSlotsForDay.length === 0) return [];
    const chips: { start: number; end: number; label: string }[] = [];
    for (const slot of tutorSlotsForDay) {
      for (let t = slot.start; t + 60 <= slot.end; t += GRANULARITY) {
        chips.push({
          start: t,
          end: t + 60,
          label: `${minutesToTimeStr(t)} – ${minutesToTimeStr(t + 60)}`,
        });
      }
    }
    return chips;
  }, [tutorSlotsForDay]);

  const handleChipClick = (start: number, end: number) => {
    dispatch({ type: 'SET_LESSON_TIME', payload: minutesToTimeStr(start) });
    dispatch({ type: 'SET_LESSON_END_TIME', payload: minutesToTimeStr(end) });
  };

  // --- Render ---
  return (
    <>
      {/* Timezone context */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', margin: '0 0 0.25rem', gap: '0.5rem' }}>
        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: 0, lineHeight: 1.4 }}>
          Times in {tzEntries.find(e => e.tz === inputTz)?.label || friendlyTzName(inputTz)}
        </p>
        {tzSwitchButtons.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flexShrink: 0 }}>
            {tzSwitchButtons.map(btn => (
              <button
                key={btn.key}
                type="button"
                onClick={() => dispatch({ type: 'SET_ACTIVE_INPUT_TZ', payload: btn.tz })}
                style={{
                  all: 'unset',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.4rem',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  border: '1px solid var(--colors-sand7, #d4d4d4)',
                  color: 'var(--colors-sand12, #1a1a1a)',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--colors-sand4, #f0f0f0)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Globe size={12} />
                Book in {btn.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <FormGroup removeSeparator={true}>
        <Input
          css={{ marginBottom: '1.5rem', marginTop: '1rem' }}
          type="date"
          id="lessonDate"
          value={lessonDate}
          min={today}
          onChange={handleDateChange}
          onKeyDown={blockPickerHotkeys}
        />

        {/* Available slots for selected day (shown in input tz) */}
        {availabilityChips.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
              {tutorName ? `${tutorName}'s` : "Tutor's"} available slots:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {availabilityChips.map((chip) => {
                const isSelected = lessonTime === minutesToTimeStr(chip.start) && lessonEndTime === minutesToTimeStr(chip.end);
                return (
                  <button
                    key={chip.start}
                    type="button"
                    onClick={() => handleChipClick(chip.start, chip.end)}
                    style={{
                      padding: '0.3rem 0.6rem',
                      fontSize: '0.75rem',
                      borderRadius: 6,
                      border: isSelected ? '1.5px solid var(--colors-blue9)' : '1px solid #d1d5db',
                      background: isSelected ? 'var(--colors-blue3)' : '#fff',
                      color: isSelected ? 'var(--colors-blue11)' : '#374151',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Label css={{ marginBottom: '0.5rem' }} htmlFor="lessonTime">
              Lesson start
            </Label>
            <Input
              type="time"
              id="lessonTime"
              value={lessonTime}
              onChange={handleTimeChange}
              onKeyDown={blockPickerHotkeys}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label css={{ marginBottom: '0.5rem' }} htmlFor="lessonEndTime">
              Lesson end
            </Label>
            <Input
              type="time"
              id="lessonEndTime"
              value={lessonEndTime}
              onChange={handleEndTimeChange}
              onKeyDown={blockPickerHotkeys}
            />
          </div>
        </div>
        {endBeforeStart && (
          <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
            End time cannot be the same as start time.
          </p>
        )}
        {startInPast && !endBeforeStart && (
          <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
            Lesson cannot start in the past.
          </p>
        )}
        {outsideAvailability && !endBeforeStart && !startInPast && !conflictMessage && (
          <p style={{ color: '#d97706', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
            This time is outside {tutorName ? `${tutorName}'s` : "the tutor's"} stated availability — they may not be free.
          </p>
        )}
        {conflictMessage && !endBeforeStart && !startInPast && (
          <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
            {conflictMessage} Please choose a different time.
          </p>
        )}
        <DateTimePreview>{lessonTimePreview}</DateTimePreview>
        {tutorTimePreview && (
          <p style={{ color: '#6b7280', fontSize: '0.8rem', textAlign: 'center', marginTop: '0.25rem' }}>
            {tutorName ? `${tutorName}'s` : "Tutor's"} time: {tutorTimePreview}
          </p>
        )}
      </FormGroup>
    </>
  );
};
